import { context as otContext, propagation, type BaggageEntry } from "@opentelemetry/api";
import { useEffect, useMemo } from "react";
import { getOtelContext, setOtelContext } from "./otelContextStore";

/**
 * Imperatively set the global OTEL baggage by merging or replacing keys.
 * Returns a restore function to revert to the previous context.
 */
export function setBaggage(values: Record<string, string>, opts?: { merge?: boolean }): () => void {
  const merge = opts?.merge !== false;
  const prevCtx = getOtelContext() ?? otContext.active();
  const prevBag = propagation.getBaggage(prevCtx);

  // Build base members from existing baggage if merging
  const baseMembers: Record<string, BaggageEntry> = {};
  if (merge && prevBag) {
    // Follow the official API: getAllEntries(): [string, BaggageEntry][]
    prevBag.getAllEntries().forEach(([k, entry]) => {
      baseMembers[k] = { value: entry.value, metadata: entry.metadata };
    });
  }

  // Diff we are applying
  const appliedKeys: string[] = [];
  Object.entries(values).forEach(([k, v]) => {
    baseMembers[k] = { value: v };
    appliedKeys.push(k);
  });

  const nextMembers = baseMembers;

  const nextBag = propagation.createBaggage(nextMembers);
  const nextCtx = propagation.setBaggage(prevCtx, nextBag);
  setOtelContext(nextCtx);

  // Return a restore callback that reverts only the keys we changed
  return () => {
    const currentCtx = getOtelContext() ?? otContext.active();
    const currentBag = propagation.getBaggage(currentCtx);
    const restoreMembers: Record<string, BaggageEntry> = {};

    // Start with current members
    if (currentBag) {
      currentBag.getAllEntries().forEach(([k, entry]) => {
        restoreMembers[k] = { value: entry.value, metadata: entry.metadata };
      });
    }

    // For each applied key, restore previous value (and metadata) or delete
    appliedKeys.forEach((k) => {
      const prevEntry = prevBag?.getEntry(k);
      if (!prevEntry) {
        delete restoreMembers[k];
      } else {
        restoreMembers[k] = {
          value: prevEntry.value,
          metadata: prevEntry.metadata,
        };
      }
    });

    const restoredBag = propagation.createBaggage(restoreMembers);
    const restoredCtx = propagation.setBaggage(currentCtx, restoredBag);
    setOtelContext(restoredCtx);
  };
}

/**
 * Serialise the current OTEL context into propagation headers (`baggage`,
 * `traceparent`, …) ready to merge into an outgoing request.
 *
 * Transport-agnostic on purpose: the caller owns the wiring, so the same
 * primitive serves Apollo links, `fetch`, and websocket handshakes without this
 * package taking a dependency on any of them.
 */
export function getBaggageHeaders(): Record<string, string> {
  const headers: Record<string, string> = {};
  propagation.inject(getOtelContext(), headers);
  return headers;
}

/**
 * React hook to set baggage for the lifetime of a component.
 * Restores the previous context on unmount.
 */
export function useSetBaggage(values?: Record<string, string> | null) {
  // Build a stable key based on sorted entries to avoid re-runs from key order or new object identities
  const stableValuesKey = useMemo(() => {
    if (!values) return "";
    const entries = Object.entries(values).map(([k, v]) => [k, v] as [string, string]);
    entries.sort(([a], [b]) => {
      if (a === b) return 0;
      return a < b ? -1 : 1;
    });
    return JSON.stringify(entries);
  }, [values]);

  useEffect(() => {
    if (!values || Object.keys(values).length === 0) return undefined;
    const restore = setBaggage(values, { merge: true });
    return () => restore();
  }, [stableValuesKey, values]);
}
